import { memo, useMemo, useState } from 'react';
import { Modal, ModalBody, ModalHeader, Button } from 'shards-react';
import { connect } from 'react-redux';
import 'react-dropzone-uploader/dist/styles.css';
import Dropzone from 'react-dropzone-uploader';
import DropzonePreview from './DropzonePreview';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import axios from 'axios';

const MAX_FILES = process.env.MAX_FILES || 12;
const UploadDocumentModal = ({ serviceType, triggerListChange, ...props }) => {
    return serviceType === 'detect_types' 
        ? <UploadDocumentModalDetectTypes serviceType={serviceType} triggerListChange={triggerListChange} {...props} /> 
        : <UploadDocumentModalSimple serviceType={serviceType} triggerListChange={triggerListChange} {...props} />;
};

//for detect_types services
const UploadDocumentModalDetectTypes = ({ mandant, uploadName, api_key, serviceID, setUploadDocumentOpen, triggerListChange }) => {
    const getHost = `${window.location.origin}/api`;
    const cssStyles = useMemo(() => ({
        dropzone: { overflow: 'unset' },
        inputLabel: { color: '#0299B4' },
    }), []);

    const [uploadedFileJobs, setUploadedFileJobs] = useState([]);
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [filesCount, setFilesCount] = useState(0);
    const [warningMessage, setWarningMessage] = useState("");

    const getUploadParams = () => ({
        url: `${getHost}/watcher/uploadFile`,
        headers: {
            'X-SYNOP-MANDANT': mandant,
            'X-SYNOP-API-KEY': api_key,
        },
    });

    const handleChangeStatus = ({ meta, xhr }, status) => {
        if (filesCount >= 10) {
            setWarningMessage(`Sie haben die maximale Anzahl von 10 Dateien erreicht`);
            return; 
        }
        if (status === 'done') {
            const response = JSON.parse(xhr.response);
            const { createdJobId } = response.infos || {};
            if (createdJobId) {
                setUploadedFileJobs(currentJobs => {
                    const newJobs = [
                        ...currentJobs, 
                        { fileJobId: createdJobId, fileName: meta.name }
                    ];
                    setFilesCount(newJobs.length); // Update filesCount with the new length
                    setWarningMessage("");
                    return newJobs;
                });
            } else {
                console.error('Error during file upload');
            }
        } else if (status === 'error') {
            console.error(`Error uploading file: ${meta.name}`);
        }
    };
    

    const onDragEnd = (result) => {
        if (!result.destination) return;

        const reorderedFiles = Array.from(uploadedFileJobs);
        const [movedFile] = reorderedFiles.splice(result.source.index, 1);
        reorderedFiles.splice(result.destination.index, 0, movedFile);

        setUploadedFileJobs(reorderedFiles);
    };

    const removeFile = (index) => {
        setUploadedFileJobs(currentJobs => currentJobs.filter((_, i) => i !== index));
    };

    const createJobFromUploadedFiles = async () => {
        try {
            const response = await axios.post(`${getHost}/watcher/createJobFromUploadedFiles`, {
                mandant,
                service: serviceID,
                upload_user: uploadName,
                fileJobs: uploadedFileJobs,
            }, {
                headers: {
                    'X-SYNOP-MANDANT': mandant,
                    'X-SYNOP-API-KEY': api_key,
                },
            });

            if (response.data.infos && response.data.infos.createdJobId) {
                console.log(`Job created successfully. JobId: ${response.data.infos.createdJobId}`);
                setIsSubmitted(true);
            } else {
                console.error('Error during job creation');
            }
        } catch (err) {
            console.error('Error during job creation', err);
        }
    };

    return (
        <Modal open={true} toggle={() => setUploadDocumentOpen(false)} centered>
            <ModalHeader toggle={() => setUploadDocumentOpen(false)}>Dokument Hochladen</ModalHeader>
            <ModalBody className='px-4'>
                <p>Ausgewählte Dokumente: {filesCount}</p>
                {warningMessage && <p className="text-danger">{warningMessage}</p>}
                <Dropzone
                    PreviewComponent={(props) => <DropzonePreview {...props} isSubmitted={isSubmitted} setUploadDocumentOpen={setUploadDocumentOpen} triggerListChange={triggerListChange} />}
                    getUploadParams={getUploadParams}
                    onChangeStatus={handleChangeStatus}
                    accept='.pdf'
                    maxFiles={MAX_FILES}
                    multiple={true}
                    inputContent='Dokumente auswählen'
                    inputWithFilesContent='Dateien hinzufügen'
                    styles={cssStyles}
                />

                {uploadedFileJobs.length > 0 && (
                    <>
                        <DragDropContext onDragEnd={onDragEnd}>
                            <Droppable droppableId="files">
                                {(provided) => (
                                    <div {...provided.droppableProps} ref={provided.innerRef} className="mt-4">
                                        {uploadedFileJobs.map((file, index) => (
                                            <Draggable key={file.fileJobId} draggableId={file.fileJobId} index={index}>
                                                {(provided) => (
                                                    <div
                                                        ref={provided.innerRef}
                                                        {...provided.draggableProps}
                                                        {...provided.dragHandleProps}
                                                        className="file-item d-flex align-items-center mb-2"
                                                        style={{
                                                            border: '1px solid #ddd',
                                                            padding: '10px',
                                                            borderRadius: '5px',
                                                            marginBottom: '8px',
                                                            backgroundColor: '#f9f9f9',
                                                            ...provided.draggableProps.style
                                                        }}
                                                    >
                                                        <span className="flex-grow-1">{file.fileName}</span>
                                                        {uploadedFileJobs.length > 1 && ( // Only show the delete button if there is more than one file
                                                            <button 
                                                                onClick={() => removeFile(index)} 
                                                                className="btn btn-danger btn-sm"
                                                            >
                                                                Löschen
                                                            </button>
                                                        )}
                                                    </div>
                                                )}
                                            </Draggable>
                                        ))}
                                        {provided.placeholder}
                                    </div>
                                )}
                            </Droppable>
                        </DragDropContext>

                        <div className="text-center mt-4">
                            <Button onClick={createJobFromUploadedFiles}>Absenden</Button>
                        </div>
                    </>
                )}
            </ModalBody>
        </Modal>
    );
};

//for sign services
const UploadDocumentModalSimple = ({ mandant, uploadName, api_key, serviceID, setUploadDocumentOpen, serviceType, triggerListChange }) => {
    const getHost = `${window.location.origin}/api`;
    const cssStyles = useMemo(() => ({
        dropzone: { overflow: 'unset' },
        inputLabel: { color: '#0299B4' },
    }), []);

    const getUploadParams = () => ({
        url: `${getHost}/watcher/uploadJob?mandant=${mandant}&service=${serviceID}&upload_user=${uploadName}`,
        headers: {
            'X-SYNOP-MANDANT': mandant,
            'X-SYNOP-API-KEY': api_key,
        },
    });

    return (
        <Modal open={true} toggle={() => setUploadDocumentOpen(false)} centered>
            <ModalHeader toggle={() => setUploadDocumentOpen(false)}>Dokument Hochladen</ModalHeader>
            <ModalBody className='px-4'>
                <Dropzone 
                    PreviewComponent={(props) => <DropzonePreview {...props} isSubmitted={true} serviceType={serviceType} setUploadDocumentOpen={setUploadDocumentOpen} triggerListChange={triggerListChange} />} 
                    getUploadParams={getUploadParams} 
                    accept='.pdf' 
                    maxFiles={1} 
                    multiple={false} 
                    inputContent='Dokument auswählen' 
                    styles={cssStyles} 
                />
            </ModalBody>
        </Modal>
    );
};

const mapStateToProps = ({ userData: { mandant, uploadName, api_key } }) => ({ mandant, uploadName, api_key });

export default connect(mapStateToProps)(memo(UploadDocumentModal));
